/**
 * CAT Service
 *
 * Service for managing CAT (Comunicacao de Acidente de Trabalho) records.
 *
 * @version 2025.1.0
 */

import apiClient from './apiClient';
import {
  Cat,
  CatListResponse,
  CatFilters,
  CreateCatRequest,
  UpdateCatRequest,
  CatStats,
  CatStatus,
  PontoDoDiaCat
} from '../types/cat';

/**
 * Extract data from API response
 * Backend returns: { message: "...", data: {...} }
 */
const extractData = (response: any) => {
  return response.data?.data || response.data;
};

/**
 * CAT Service
 */
class CatService {
  private baseUrl = '/cats';

  // ========== CRUD OPERATIONS ==========

  /**
   * Get all CATs with pagination and filters
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    filters?: CatFilters;
  }): Promise<CatListResponse> {
    console.log('📋 CatService.getAll - Fetching CATs', params);

    const queryParams: Record<string, string | number> = {};

    if (params?.page) queryParams.page = params.page;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    if (params?.filters) {
      if (params.filters.search) queryParams.search = params.filters.search;
      if (params.filters.companyId) queryParams.companyId = params.filters.companyId;
      if (params.filters.employeeId) queryParams.employeeId = params.filters.employeeId;
      if (params.filters.status?.length) {
        queryParams.status = params.filters.status.join(',');
      }
      if (params.filters.reportType) queryParams.reportType = params.filters.reportType;
      if (params.filters.accidentType) queryParams.accidentType = params.filters.accidentType;
      if (params.filters.startDate) queryParams.startDate = params.filters.startDate;
      if (params.filters.endDate) queryParams.endDate = params.filters.endDate;
    }

    const response = await apiClient.get(this.baseUrl, { params: queryParams });
    console.log('✅ CatService.getAll - Success', response.data);
    return response.data;
  }

  /**
   * Get CAT by ID
   */
  async getById(id: number): Promise<Cat> {
    console.log('📋 CatService.getById - Fetching CAT', id);
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    console.log('✅ CatService.getById - Success', response.data);
    return extractData(response);
  }

  /**
   * Create new CAT
   */
  async create(data: CreateCatRequest): Promise<Cat> {
    console.log('📋 CatService.create - Creating CAT', data);
    const response = await apiClient.post(this.baseUrl, data);
    console.log('✅ CatService.create - Success', response.data);
    return extractData(response);
  }

  /**
   * Update CAT
   */
  async update(id: number, data: UpdateCatRequest): Promise<Cat> {
    console.log('📋 CatService.update - Updating CAT', id, data);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    console.log('✅ CatService.update - Success', response.data);
    return extractData(response);
  }

  /**
   * Cancel CAT
   */
  async cancel(id: number): Promise<Cat> {
    console.log('📋 CatService.cancel - Cancelling CAT', id);
    const response = await apiClient.patch(`${this.baseUrl}/${id}/cancel`);
    console.log('✅ CatService.cancel - Success', response.data);
    return extractData(response);
  }

  /**
   * Submit CAT
   */
  async submit(id: number): Promise<Cat> {
    console.log('📋 CatService.submit - Submitting CAT', id);
    const response = await apiClient.patch(`${this.baseUrl}/${id}/submit`);
    console.log('✅ CatService.submit - Success', response.data);
    return extractData(response);
  }

  /**
   * Get CAT statistics
   */
  async getStats(companyId?: number): Promise<CatStats> {
    console.log('📋 CatService.getStats - Fetching stats', companyId);

    const queryParams: Record<string, number> = {};
    if (companyId) queryParams.companyId = companyId;

    const response = await apiClient.get(`${this.baseUrl}/stats`, { params: queryParams });
    console.log('✅ CatService.getStats - Success', response.data);
    return extractData(response);
  }

  /**
   * Generate (or fetch the cached) printable PDF for a CAT (#248).
   * Returns the relative path under /uploads to open in a new tab.
   */
  async generatePdf(id: number): Promise<{ pdfPath: string }> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/pdf`);
    return extractData(response);
  }

  /**
   * Marcações de ponto do dia do acidente relatado na CAT.
   */
  async getPontoDoDia(id: number): Promise<PontoDoDiaCat> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/ponto-do-dia`);
    return extractData(response);
  }

  // ========== HELPER METHODS ==========

  /**
   * Get CATs by company
   */
  async getByCompany(
    companyId: number,
    params?: {
      page?: number;
      limit?: number;
      status?: CatStatus[];
    }
  ): Promise<CatListResponse> {
    return this.getAll({
      ...params,
      filters: {
        companyId,
        status: params?.status
      }
    });
  }

  /**
   * Get CATs by employee
   */
  async getByEmployee(
    employeeId: number,
    params?: {
      page?: number;
      limit?: number;
    }
  ): Promise<CatListResponse> {
    return this.getAll({
      ...params,
      filters: {
        employeeId
      }
    });
  }

  /**
   * Get draft CATs
   */
  async getDrafts(companyId?: number): Promise<CatListResponse> {
    return this.getAll({
      filters: {
        companyId,
        status: [CatStatus.DRAFT]
      }
    });
  }

  /**
   * Get overdue CATs (drafts older than 1 business day)
   * Note: This is calculated on the frontend based on the data
   */
  async getOverdue(companyId?: number): Promise<Cat[]> {
    const response = await this.getDrafts(companyId);
    const now = new Date();

    return response.data.filter(cat => {
      const accidentDate = new Date(cat.accidentDate);
      const diffTime = Math.abs(now.getTime() - accidentDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      // Simple check: more than 1 day (not accounting for business days here)
      return diffDays > 1;
    });
  }
}

export const catService = new CatService();
export default catService;
